import { getPhysicalBoundsForCellBounds } from 'features/dataLayers/generation/shortTermCorrosionRate/algorithmShared'
import { createFeatureRectangleFromCellBounds } from 'features/dataLayers/generation/shortTermCorrosionRate/featureExtraction'
import { getCellBoundsAreaFeetSquared } from 'features/dataLayers/generation/shortTermCorrosionRate/geometry'
import type {
  ShortTermCorrosionCellBounds,
  ShortTermCorrosionFeatureRectangle,
  ShortTermCorrosionPriorSupport,
  ShortTermRateFeatureBounds,
  ShortTermRateFeatureGridMetadata,
} from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import {
  type DeficiencyMarkupBounds,
  type GeckoComponentDataFrame,
  type GeckoComponentDeficiency,
  GeckoComponentRect,
  GeckoMarkupLayer,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  type RolodexEntryJson,
  RolodexEntryType,
} from 'types'

export type ShortTermCorrosionWorkflowMarkupReferenceData = {
  site?: {
    tags?: {
      organization?: string
    }
  }
  siteID?: string
  unitID?: string
}

export const FEATURE_BOUNDARY_COLORS = {
  observation: '#E5E7EB',
  detected: '#A78BFA',
  selected: '#22D3EE',
} as const

export function getObservationFeatureRectangles({
  componentDeficiencies,
  componentId,
  inspectionId,
  targetGrid,
}: {
  componentDeficiencies: readonly GeckoComponentDeficiency[]
  componentId: string
  inspectionId: string
  targetGrid: ShortTermRateFeatureGridMetadata
}): ShortTermCorrosionFeatureRectangle[] {
  const rectangles: ShortTermCorrosionFeatureRectangle[] = []

  for (const deficiency of componentDeficiencies) {
    if (deficiency.tags.component !== componentId) continue
    if (deficiency.tags.inspection !== inspectionId) continue
    if (!deficiency.data.markupBounds?.length) continue

    const unionedMarkupBounds = deficiency.data.markupBounds.reduce(
      (acc, bounds) => ({
        x_start: Math.min(acc.x_start, bounds.x_start),
        y_start: Math.min(acc.y_start, bounds.y_start),
        x_end: Math.max(acc.x_end, bounds.x_end),
        y_end: Math.max(acc.y_end, bounds.y_end),
        unit: acc.unit,
      })
    )

    const targetCellBounds = getCellBoundsForMarkupBounds(
      unionedMarkupBounds,
      targetGrid
    )
    if (!targetCellBounds) continue

    rectangles.push(
      createFeatureRectangleFromCellBounds({
        id: `observation-${deficiency.id}`,
        source: 'observation',
        label: deficiency.data.title || 'Observation',
        targetCellBounds,
        seedCellCount: getCellCount(targetCellBounds),
        grid: targetGrid,
      })
    )
  }

  return rectangles
}

export function createPriorSupportRectangle({
  featureRectangle,
  support,
  comparisonGrid,
}: {
  featureRectangle: ShortTermCorrosionFeatureRectangle
  support: ShortTermCorrosionPriorSupport
  comparisonGrid: ShortTermRateFeatureGridMetadata
}): ShortTermCorrosionFeatureRectangle {
  return {
    ...featureRectangle,
    targetCellBounds: support.priorCellBounds,
    targetBoundsFeet: getPhysicalBoundsForCellBounds(
      support.priorCellBounds,
      comparisonGrid
    ),
    areaFeetSquared: getCellBoundsAreaFeetSquared(
      support.priorCellBounds,
      comparisonGrid
    ),
  }
}

export function buildFeatureRectangleMarkupLayer({
  featureRectangle,
  componentId,
  referenceData,
  isSelected,
}: {
  featureRectangle: ShortTermCorrosionFeatureRectangle
  componentId: string
  referenceData: ShortTermCorrosionWorkflowMarkupReferenceData
  isSelected: boolean
}): GeckoMarkupLayer {
  return buildShortTermFeatureMarkupLayer({
    id: featureRectangle.id,
    bounds: featureRectangleToMarkupBounds(featureRectangle),
    name: featureRectangle.label ?? featureRectangle.id,
    color: getFeatureBoundaryColor(featureRectangle, isSelected),
    tags: {
      componentId,
      organizationId: referenceData.site?.tags?.organization ?? '',
      siteId: referenceData.siteID,
      unitId: referenceData.unitID,
    },
    showLabel: false,
  })
}

function buildShortTermFeatureMarkupLayer({
  id,
  bounds,
  name,
  color,
  tags,
  showLabel = false,
}: {
  id: string
  bounds: DeficiencyMarkupBounds
  name: string
  color: string
  tags: {
    componentId: string
    organizationId: string
    siteId?: string
    unitId?: string
  }
  showLabel?: boolean
}): GeckoMarkupLayer {
  const json: RolodexEntryJson = {
    id: `deficiency-${id}`,
    type: RolodexEntryType.MARKUP_LAYER,
    data: {
      name,
      color,
      category: 'user-added',
      geometry: {
        x_measurement_unit: bounds.unit,
        y_measurement_unit: bounds.unit,
      },
      primitives: {
        [id]: {
          kind: 'rect',
          x_start: bounds.x_start,
          y_start: bounds.y_start,
          x_end: bounds.x_end,
          y_end: bounds.y_end,
        },
        ...(showLabel
          ? {
              [`title/${id}`]: {
                kind: 'label',
                x: bounds.x_start + (bounds.x_end - bounds.x_start) / 2,
                y: bounds.y_start + (bounds.y_end - bounds.y_start) / 2,
                text: name,
              },
            }
          : {}),
      },
      schema_version: 5,
    },
    tags: {
      component: tags.componentId,
      organization: tags.organizationId,
      site: tags.siteId ?? '',
      unit: tags.unitId ?? '',
    },
    index: {},
    generation: 1,
    updated_at: new Date().toISOString(),
    access_restriction: null,
  }

  return GeckoMarkupLayer.CreateFromJSON(json)
}

export function getFeatureBoundaryColor(
  featureRectangle: ShortTermCorrosionFeatureRectangle,
  isSelected: boolean
): string {
  if (isSelected) return FEATURE_BOUNDARY_COLORS.selected
  return FEATURE_BOUNDARY_COLORS[featureRectangle.source]
}

export function featureRectangleToMarkupBounds(
  featureRectangle: ShortTermCorrosionFeatureRectangle
): DeficiencyMarkupBounds {
  return {
    unit: GeckoMeasurementUnit.Feet.id,
    x_start: featureRectangle.targetBoundsFeet.minXFeet,
    x_end: featureRectangle.targetBoundsFeet.maxXFeet,
    y_start: featureRectangle.targetBoundsFeet.minYFeet,
    y_end: featureRectangle.targetBoundsFeet.maxYFeet,
  }
}

export function getCellBoundsForMarkupBounds(
  bounds: DeficiencyMarkupBounds,
  targetGrid: ShortTermRateFeatureGridMetadata
): ShortTermCorrosionCellBounds | undefined {
  const measurementUnit =
    GeckoMeasurementUnit.GetByID(bounds.unit) ?? GeckoMeasurementUnit.Feet
  const minXFeet = GeckoMeasurement.CreateFromValue(
    Math.min(bounds.x_start, bounds.x_end),
    measurementUnit
  ).convertTo(GeckoMeasurementUnit.Feet).num
  const maxXFeet = GeckoMeasurement.CreateFromValue(
    Math.max(bounds.x_start, bounds.x_end),
    measurementUnit
  ).convertTo(GeckoMeasurementUnit.Feet).num
  const minYFeet = GeckoMeasurement.CreateFromValue(
    Math.min(bounds.y_start, bounds.y_end),
    measurementUnit
  ).convertTo(GeckoMeasurementUnit.Feet).num
  const maxYFeet = GeckoMeasurement.CreateFromValue(
    Math.max(bounds.y_start, bounds.y_end),
    measurementUnit
  ).convertTo(GeckoMeasurementUnit.Feet).num

  const rawBounds = {
    minX: Math.floor(
      (minXFeet - targetGrid.xStartFeet) / targetGrid.binWidthFeet
    ),
    maxX:
      Math.ceil((maxXFeet - targetGrid.xStartFeet) / targetGrid.binWidthFeet) -
      1,
    minY: Math.floor(
      (minYFeet - targetGrid.yStartFeet) / targetGrid.binHeightFeet
    ),
    maxY:
      Math.ceil((maxYFeet - targetGrid.yStartFeet) / targetGrid.binHeightFeet) -
      1,
  }

  if (
    rawBounds.maxX < 0 ||
    rawBounds.minX >= targetGrid.xBinCount ||
    rawBounds.maxY < 0 ||
    rawBounds.minY >= targetGrid.yBinCount
  ) {
    return undefined
  }

  return {
    minX: Math.max(0, Math.min(rawBounds.minX, targetGrid.xBinCount - 1)),
    maxX: Math.max(0, Math.min(rawBounds.maxX, targetGrid.xBinCount - 1)),
    minY: Math.max(0, Math.min(rawBounds.minY, targetGrid.yBinCount - 1)),
    maxY: Math.max(0, Math.min(rawBounds.maxY, targetGrid.yBinCount - 1)),
  }
}

export function getFeatureGridMetadata(
  cdf: GeckoComponentDataFrame
): ShortTermRateFeatureGridMetadata {
  return {
    xStartFeet: cdf.data.geometry.start.x.convertTo(GeckoMeasurementUnit.Feet)
      .num,
    yStartFeet: cdf.data.geometry.start.y.convertTo(GeckoMeasurementUnit.Feet)
      .num,
    binWidthFeet: cdf.data.binning.bin_size.width.convertTo(
      GeckoMeasurementUnit.Feet
    ).num,
    binHeightFeet: cdf.data.binning.bin_size.height.convertTo(
      GeckoMeasurementUnit.Feet
    ).num,
    xBinCount: cdf.data.binning.x_bin_count,
    yBinCount: cdf.data.binning.y_bin_count,
  }
}

export function getCombinedBounds(
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
): ShortTermRateFeatureBounds | undefined {
  if (featureRectangles.length === 0) return undefined

  let minXFeet = Number.POSITIVE_INFINITY
  let maxXFeet = Number.NEGATIVE_INFINITY
  let minYFeet = Number.POSITIVE_INFINITY
  let maxYFeet = Number.NEGATIVE_INFINITY

  for (const featureRectangle of featureRectangles) {
    minXFeet = Math.min(minXFeet, featureRectangle.targetBoundsFeet.minXFeet)
    maxXFeet = Math.max(maxXFeet, featureRectangle.targetBoundsFeet.maxXFeet)
    minYFeet = Math.min(minYFeet, featureRectangle.targetBoundsFeet.minYFeet)
    maxYFeet = Math.max(maxYFeet, featureRectangle.targetBoundsFeet.maxYFeet)
  }

  return { minXFeet, maxXFeet, minYFeet, maxYFeet }
}

export function getPaddedFeatureZoomRect(
  componentId: string,
  bounds: ShortTermRateFeatureBounds
): GeckoComponentRect {
  const xSpan = Math.max(bounds.maxXFeet - bounds.minXFeet, 1)
  const ySpan = Math.max(bounds.maxYFeet - bounds.minYFeet, 1)
  const paddingX = xSpan * 1.2
  const paddingY = ySpan * 1.2

  return GeckoComponentRect.CreateFromXYs(
    componentId,
    bounds.minXFeet - paddingX,
    bounds.minYFeet - paddingY,
    bounds.maxXFeet + paddingX,
    bounds.maxYFeet + paddingY,
    GeckoMeasurementUnit.Feet,
    GeckoMeasurementUnit.Feet
  )
}

export function getCellCount(cellBounds: ShortTermCorrosionCellBounds): number {
  return (
    (cellBounds.maxX - cellBounds.minX + 1) *
    (cellBounds.maxY - cellBounds.minY + 1)
  )
}
